import React, { useState, useEffect } from "react";

const TabSystem = () => {
  return (
    <><App /></>
  )
}

export default TabSystem

import {
  ShoppingCart,
  Users,
  Package,
  CreditCard,
  Minus,
  Plus,
  Trash2,
  Search,
  AlertTriangle,
  Check,
  User,
  Edit,
  X,
  UserPlus,
  Eye,
  PackagePlus,
  Settings,
  TrendingUp,
  TrendingDown,
  Crown,
  Award,
  Star,
  Calendar,
  BarChart3,
  FileSpreadsheet,
  Shield,
  UserCheck,
  UserX,
  Key,
  Banknote,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import "@/App.css";
import LoginForm from "@/components/LoginForm";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

// Product Management Component
function ProductManagement({ token, user, onProductsChanged = () => {} }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  // const [isStockDialogOpen, setIsStockDialogOpen] = useState(false);
  const [productForm, setProductForm] = useState({
    name: "",
    category: "",
    price: "",
    description: "",
    stock_quantity: "",
    low_stock_threshold: 5,
  });
  const [stockAdjustment, setStockAdjustment] = useState({
    adjustment_type: "add",
    quantity: "",
    reason: "",
  });

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchActiveProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/active-products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...productForm,
          price: parseFloat(productForm.price),
          stock_quantity: parseInt(productForm.stock_quantity),
          low_stock_threshold: parseInt(productForm.low_stock_threshold),
        }),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setProductForm({
          name: "",
          category: "",
          price: "",
          description: "",
          stock_quantity: "",
          low_stock_threshold: 5,
        });
        fetchProducts();
        fetchCategories();
        onProductsChanged();
        // Notify other parts of the app (e.g., POS) that products have changed
        window.dispatchEvent(new Event("products-updated"));
      }
    } catch (error) {
      console.error("Error creating product:", error);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...productForm,
        price: productForm.price ? parseFloat(productForm.price) : undefined,
        stock_quantity: productForm.stock_quantity
          ? parseInt(productForm.stock_quantity)
          : undefined,
        low_stock_threshold: productForm.low_stock_threshold
          ? parseInt(productForm.low_stock_threshold)
          : undefined,
      };

      const response = await fetch(
        `${BACKEND_URL}/api/products/${selectedProduct.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedProduct(null);
        fetchProducts();
        onProductsChanged();
        // Notify other parts of the app (e.g., POS) that products have changed
        window.dispatchEvent(new Event("products-updated"));
      }
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (user?.role !== "admin") {
      alert("Access denied. Only admin users can delete products.");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete "${productName}"? This action cannot be undone.`
      )
    ) {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/products/${productId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.ok) {
          fetchProducts();
          onProductsChanged();
          // Notify other parts of the app (e.g., POS) that products have changed
          window.dispatchEvent(new Event("products-updated"));
        } else {
          const error = await response.json();
          alert(error.detail || "Failed to delete product");
        }
      } catch (error) {
        console.error("Error deleting product:", error);
        alert("Error deleting product");
      }
    }
  };

  // const handleStockAdjustment = async (e) => {
  //   e.preventDefault();
  //   try {
  //     const response = await fetch(
  //       `${BACKEND_URL}/api/products/${selectedProduct.id}/adjust-stock`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Bearer ${token}`,
  //         },
  //         body: JSON.stringify({
  //           ...stockAdjustment,
  //           quantity: parseInt(stockAdjustment.quantity),
  //         }),
  //       }
  //     );

  //     if (response.ok) {
  //       setIsStockDialogOpen(false);
  //       setStockAdjustment({
  //         adjustment_type: "add",
  //         quantity: "",
  //         reason: "",
  //       });
  //       setSelectedProduct(null);
  //       fetchProducts();
  //       // Notify other parts of the app (e.g., POS) that products have changed
  //       window.dispatchEvent(new Event("products-updated"));
  //     }
  //   } catch (error) {
  //     console.error("Error adjusting stock:", error);
  //   }
  // };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Service Package Management</h2>
          <p className="text-gray-600">
            Manage Services and other packages offered at CWC.
          </p>
        </div>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="bg-cwc-gold hover:bg-cwc-goldDark text-cwc-black"
        >
          <PackagePlus className="w-4 h-4 mr-2" />
          Add Packages
        </Button>
      </div>

      {/* Categories Overview */}
      {categories.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {categories.map((category) => (
            <Card key={category.name} className="border-l-4 border-l-cwc-gold">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {category.count} products
                    </p>
                  </div>
                  <Package className="w-8 h-8 text-cwc-goldDark" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {products.map((product) => (
          <Card key={product.id} className="transition-shadow hover:shadow-lg">
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <h3 className="font-semibold text-gray-800">
                    {product.name}
                  </h3>
                  <Badge
                    style={{ pointerEvents: "none" }}
                    variant="outline"
                    className="text-emerald-600 border-emerald-300"
                  >
                    ₱{product.price.toFixed(2)}
                  </Badge>
                </div>

                <p className="text-sm text-gray-600">{product.description}</p>

                <div className="flex items-center justify-between">
                  <div className="flex flex-row gap-1">
                    <Badge
                      style={{ pointerEvents: "none" }}
                      variant="secondary"
                      className="text-blue-800 bg-blue-100"
                    >
                      {product.category}
                    </Badge>
                    <Badge
                      style={{ pointerEvents: "none" }}
                      variant={product.is_active ? "default" : "secondary"}
                      className={product.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}
                    >
                      {product.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  {/* <Badge
                    style={{ pointerEvents: "none" }}
                    variant={
                      product.stock_quantity <= product.low_stock_threshold
                        ? "destructive"
                        : "default"
                    }
                    className={
                      product.stock_quantity <= product.low_stock_threshold
                        ? ""
                        : "bg-green-100 text-green-800"
                    }
                  >
                    Stock: {product.stock_quantity}
                  </Badge> */}
                </div>

                <div className="text-xs text-gray-500">
                  {/* <p>Low stock threshold: {product.low_stock_threshold}</p> */}
                  <p>
                    Updated:{" "}
                    {new Date(product.updated_date).toLocaleDateString()}
                  </p>
                </div>

                <div className="flex space-x-2">
                  {/* <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedProduct(product);
                      setStockAdjustment({
                        adjustment_type: "add",
                        quantity: "",
                        reason: "",
                      });
                      setIsStockDialogOpen(true);
                    }}
                    className="flex-1"
                  >
                    <Settings className="w-3 h-3 mr-1" />
                    Stock
                  </Button> */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedProduct(product);
                      setProductForm(product);
                      setIsEditDialogOpen(true);
                    }}
                    className="flex-1"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  {user?.role === "admin" && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() =>
                        handleDeleteProduct(product.id, product.name)
                      }
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Product Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProduct} className="space-y-4">
            <div>
              <Label htmlFor="name">Product Name</Label>
              <Input
                id="name"
                value={productForm.name}
                onChange={(e) =>
                  setProductForm({ ...productForm, name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={productForm.category}
                onValueChange={(value) =>
                  setProductForm({ ...productForm, category: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Wakeboard">Wakeboard</SelectItem>
                  <SelectItem value="Massage">Massage</SelectItem>
                  <SelectItem value="Swimming Pool">Swimming Pool</SelectItem>
                  <SelectItem value="Wakeboard Equipment Rental">
                    Wakeboard Equipment Rental
                  </SelectItem>
                  <SelectItem value="Membership">Membership</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="price">Price (₱)</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={productForm.price}
                onChange={(e) =>
                  setProductForm({ ...productForm, price: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={productForm.description}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    description: e.target.value,
                  })
                }
                rows={3}
                required
              />
            </div>
            {/* <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="stock_quantity">Initial Stock</Label>
                <Input
                  id="stock_quantity"
                  type="number"
                  min="0"
                  value={productForm.stock_quantity}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      stock_quantity: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="low_stock_threshold">Low Stock Threshold</Label>
                <Input
                  id="low_stock_threshold"
                  type="number"
                  min="0"
                  value={productForm.low_stock_threshold}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      low_stock_threshold: e.target.value,
                    })
                  }
                  required
                />
              </div>
            </div> */}
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
                className="border-cwc-gold text-cwc-gold hover:bg-cwc-gold hover:text-cwc-black"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-cwc-gold hover:bg-cwc-goldDark text-cwc-black"
              >
                Create Product
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateProduct} className="space-y-4">
            <div>
              <Label htmlFor="edit_name">Product Name</Label>
              <Input
                id="edit_name"
                value={productForm.name}
                onChange={(e) =>
                  setProductForm({ ...productForm, name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_is_active">Status</Label>
              <Select
                value={productForm.is_active ? "active" : "inactive"}
                onValueChange={(value) =>
                  setProductForm({ ...productForm, is_active: value === "active" })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_category">Category</Label>
              <Select
                value={productForm.category}
                onValueChange={(value) =>
                  setProductForm({ ...productForm, category: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Wakeboard">Wakeboard</SelectItem>
                  <SelectItem value="Massage">Massage</SelectItem>
                  <SelectItem value="Swimming Pool">Swimming Pool</SelectItem>
                  <SelectItem value="Wakeboard Equipment Rental">
                    Wakeboard Equipment Rental
                  </SelectItem>
                  <SelectItem value="Membership">Membership</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_price">Price (₱)</Label>
              <Input
                id="edit_price"
                type="number"
                step="0.01"
                min="0"
                value={productForm.price}
                onChange={(e) =>
                  setProductForm({ ...productForm, price: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_description">Description</Label>
              <Textarea
                id="edit_description"
                value={productForm.description}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    description: e.target.value,
                  })
                }
                rows={3}
                required
              />
            </div>
            {/* <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_stock_quantity">Current Stock</Label>
                <Input
                  id="edit_stock_quantity"
                  type="number"
                  min="0"
                  value={productForm.stock_quantity}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      stock_quantity: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_low_stock_threshold">
                  Low Stock Threshold
                </Label>
                <Input
                  id="edit_low_stock_threshold"
                  type="number"
                  min="0"
                  value={productForm.low_stock_threshold}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      low_stock_threshold: e.target.value,
                    })
                  }
                  required
                />
              </div>
            </div> */}
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
                className="border-cwc-gold text-cwc-gold hover:bg-cwc-gold hover:text-cwc-black"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-cwc-gold hover:bg-cwc-goldDark text-cwc-black"
              >
                Update Product
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Dialog */}
      {/* <Dialog open={isStockDialogOpen} onOpenChange={setIsStockDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
            <DialogDescription>
              {selectedProduct &&
                `Current stock: ${selectedProduct.stock_quantity} units`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleStockAdjustment} className="space-y-4">
            <div>
              <Label htmlFor="adjustment_type">Adjustment Type</Label>
              <Select
                value={stockAdjustment.adjustment_type}
                onValueChange={(value) =>
                  setStockAdjustment({
                    ...stockAdjustment,
                    adjustment_type: value,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="w-4 h-4 text-cwc-goldDark" />
                      <span>Add Stock</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="subtract">
                    <div className="flex items-center space-x-2">
                      <TrendingDown className="w-4 h-4 text-cwc-goldDark" />
                      <span>Subtract Stock</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="set">
                    <div className="flex items-center space-x-2">
                      <Settings className="w-4 h-4 text-cwc-goldDark" />
                      <span>Set Stock Level</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="quantity">
                {stockAdjustment.adjustment_type === "set"
                  ? "New Stock Level"
                  : "Quantity"}
              </Label>
              <Input
                id="quantity"
                type="number"
                min="0"
                value={stockAdjustment.quantity}
                onChange={(e) =>
                  setStockAdjustment({
                    ...stockAdjustment,
                    quantity: e.target.value,
                  })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="reason">Reason (Optional)</Label>
              <Textarea
                id="reason"
                value={stockAdjustment.reason}
                onChange={(e) =>
                  setStockAdjustment({
                    ...stockAdjustment,
                    reason: e.target.value,
                  })
                }
                rows={2}
                placeholder="e.g., Received new shipment, Damaged items, Stock count correction"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsStockDialogOpen(false)}
                className="border-cwc-gold text-cwc-gold hover:bg-cwc-gold hover:text-cwc-black"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-cwc-gold hover:bg-cwc-goldDark text-cwc-black"
              >
                Adjust Stock
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog> */}
    </div>
  );
}
// User Management Component
function UserManagement({ token, user }) {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    username: "",
    email: "",
    full_name: "",
    role: "cashier",
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [createPassword, setCreatePassword] = useState("");
  const [confirmCreatePassword, setConfirmCreatePassword] = useState("");

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        console.error("Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (createPassword !== confirmCreatePassword) {
      alert("Passwords do not match");
      return;
    }

    if (createPassword.length < 6) {
      alert("Password must be at least 6 characters long");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...userForm,
          password: createPassword,
        }),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setUserForm({
          username: "",
          email: "",
          full_name: "",
          role: "cashier",
        });
        setCreatePassword("");
        setConfirmCreatePassword("");
        fetchUsers();
        alert("User created successfully");
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to create user");
      }
    } catch (error) {
      console.error("Error creating user:", error);
      alert("Error creating user");
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/users/${selectedUser.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(userForm),
        }
      );

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedUser(null);
        fetchUsers();
        alert("User updated successfully");
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to update user");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Error updating user");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (
      window.confirm(
        `Are you sure you want to delete user "${userName}"? This action cannot be undone.`
      )
    ) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          fetchUsers();
          alert("User deleted successfully");
        } else {
          const error = await response.json();
          alert(error.detail || "Failed to delete user");
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Error deleting user");
      }
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    const action = currentStatus ? "deactivate" : "activate";
    if (window.confirm(`Are you sure you want to ${action} this user?`)) {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/users/${userId}/toggle-status`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.ok) {
          fetchUsers();
          const newStatus = currentStatus ? "deactivated" : "activated";
          alert(`User ${newStatus} successfully`);
        } else {
          const error = await response.json();
          alert(error.detail || `Failed to ${action} user`);
        }
      } catch (error) {
        console.error(`Error ${action}ing user:`, error);
        alert(`Error ${action}ing user`);
      }
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert("New passwords do not match");
      return;
    }

    if (passwordForm.new_password.length < 6) {
      alert("Password must be at least 6 characters long");
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/users/${selectedUser.id}/password`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            current_password: passwordForm.current_password || undefined,
            new_password: passwordForm.new_password,
          }),
        }
      );

      if (response.ok) {
        setIsPasswordDialogOpen(false);
        setPasswordForm({
          current_password: "",
          new_password: "",
          confirm_password: "",
        });
        setSelectedUser(null);
        alert("Password updated successfully");
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to update password");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      alert("Error updating password");
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "Never";
    return new Date(isoString).toLocaleString("en-PH");
  };

  const getRoleIcon = (role) => {
    return role === "admin" ? Shield : User;
  };

  const getRoleColor = (role) => {
    return "text-cwc-goldDark bg-cwc-gold/20";
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Only show to admin users
  if (user?.role !== "admin") {
    return (
      <div className="py-12 text-center">
        <Shield className="w-16 h-16 mx-auto mb-4 text-gray-400" />
        <h2 className="mb-2 text-xl font-semibold text-gray-600">
          Access Restricted
        </h2>
        <p className="text-gray-500">
          Only admin users can manage system users.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-gray-600">
            Manage system users, roles, and permissions
          </p>
        </div>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="bg-cwc-gold hover:bg-cwc-goldDark text-cwc-black"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {users.map((userItem) => {
          const RoleIcon = getRoleIcon(userItem.role);
          const roleColorClass = getRoleColor(userItem.role);

          return (
            <Card
              key={userItem.id}
              className="transition-shadow border-l-4 hover:shadow-lg border-l-cwc-gold"
            >
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-full ${roleColorClass}`}>
                        <RoleIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">
                          {userItem.full_name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          @{userItem.username}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Badge
                        className={
                          userItem.is_active
                            ? "bg-cwc-gold text-cwc-black hover:bg-cwc-gold hover:!text-cwc-black cursor-default pointer-events-none select-none"
                            : "bg-gray-200 text-gray-700 cursor-default pointer-events-none select-none"
                        }
                      >
                        {userItem.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Badge
                        variant="outline"
                        className="text-cwc-goldDark border-cwc-gold"
                      >
                        {userItem.role.charAt(0).toUpperCase() +
                          userItem.role.slice(1)}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">{userItem.email}</p>
                    <div className="text-xs text-gray-500">
                      <p>Created: {formatDateTime(userItem.created_date)}</p>
                      <p>Last Login: {formatDateTime(userItem.last_login)}</p>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedUser(userItem);
                        setUserForm({
                          username: userItem.username,
                          email: userItem.email,
                          full_name: userItem.full_name,
                          role: userItem.role,
                        });
                        setIsEditDialogOpen(true);
                      }}
                      className="flex-1 border-cwc-gold text-cwc-gold hover:bg-cwc-gold hover:text-cwc-black"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedUser(userItem);
                        setPasswordForm({
                          current_password: "",
                          new_password: "",
                          confirm_password: "",
                        });
                        setIsPasswordDialogOpen(true);
                      }}
                      className="border-cwc-gold text-cwc-gold hover:bg-cwc-gold hover:text-cwc-black"
                    >
                      <Key className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant={userItem.is_active ? "outline" : "default"}
                      onClick={() =>
                        handleToggleUserStatus(userItem.id, userItem.is_active)
                      }
                      disabled={userItem.id === user.id}
                      className={
                        userItem.is_active
                          ? "border-cwc-gold text-cwc-gold hover:bg-cwc-gold hover:text-cwc-black"
                          : "bg-cwc-gold hover:bg-cwc-goldDark text-cwc-black"
                      }
                    >
                      {userItem.is_active ? (
                        <UserX className="w-3 h-3" />
                      ) : (
                        <UserCheck className="w-3 h-3" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        handleDeleteUser(userItem.id, userItem.full_name)
                      }
                      disabled={userItem.id === user.id}
                      className="border-cwc-gold text-cwc-gold hover:bg-cwc-gold hover:text-cwc-black"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={userForm.username}
                onChange={(e) =>
                  setUserForm({ ...userForm, username: e.target.value })
                }
                required
                placeholder="Enter username"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={userForm.email}
                onChange={(e) =>
                  setUserForm({ ...userForm, email: e.target.value })
                }
                required
                placeholder="Enter email address"
              />
            </div>
            <div>
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                value={userForm.full_name}
                onChange={(e) =>
                  setUserForm({ ...userForm, full_name: e.target.value })
                }
                required
                placeholder="Enter full name"
              />
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <Select
                value={userForm.role}
                onValueChange={(value) =>
                  setUserForm({ ...userForm, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cashier">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-cwc-goldDark" />
                      <span>Cashier</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-cwc-goldDark" />
                      <span>Admin</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                required
                placeholder="Enter password (min 6 characters)"
                minLength={6}
              />
            </div>
            <div>
              <Label htmlFor="confirm_password">Confirm Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={confirmCreatePassword}
                onChange={(e) => setConfirmCreatePassword(e.target.value)}
                required
                placeholder="Confirm password"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
                className="border-cwc-gold text-cwc-gold hover:bg-cwc-gold hover:text-cwc-black"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-cwc-gold hover:bg-cwc-goldDark text-cwc-black"
              >
                Create User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div>
              <Label htmlFor="edit_username">Username</Label>
              <Input
                id="edit_username"
                value={userForm.username}
                onChange={(e) =>
                  setUserForm({ ...userForm, username: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={userForm.email}
                onChange={(e) =>
                  setUserForm({ ...userForm, email: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_full_name">Full Name</Label>
              <Input
                id="edit_full_name"
                value={userForm.full_name}
                onChange={(e) =>
                  setUserForm({ ...userForm, full_name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_role">Role</Label>
              <Select
                value={userForm.role}
                onValueChange={(value) =>
                  setUserForm({ ...userForm, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cashier">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-cwc-goldDark" />
                      <span>Cashier</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-cwc-goldDark" />
                      <span>Admin</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
                className="border-cwc-gold text-cwc-gold hover:bg-cwc-gold hover:text-cwc-black"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-cwc-gold hover:bg-cwc-goldDark text-cwc-black"
              >
                Update User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Update Dialog */}
      <Dialog
        open={isPasswordDialogOpen}
        onOpenChange={setIsPasswordDialogOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Password</DialogTitle>
            <DialogDescription>
              {selectedUser && `Update password for ${selectedUser.full_name}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            {selectedUser && selectedUser.id === user.id && (
              <div>
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      current_password: e.target.value,
                    })
                  }
                  required
                  placeholder="Enter current password"
                />
              </div>
            )}
            <div>
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) =>
                  setPasswordForm({
                    ...passwordForm,
                    new_password: e.target.value,
                  })
                }
                required
                placeholder="Enter new password (min 6 characters)"
                minLength={6}
              />
            </div>
            <div>
              <Label htmlFor="confirm_new_password">Confirm New Password</Label>
              <Input
                id="confirm_new_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) =>
                  setPasswordForm({
                    ...passwordForm,
                    confirm_password: e.target.value,
                  })
                }
                required
                placeholder="Confirm new password"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsPasswordDialogOpen(false)}
                className="border-cwc-gold text-cwc-gold hover:bg-cwc-gold hover:text-cwc-black"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-cwc-gold hover:bg-cwc-goldDark text-cwc-black"
              >
                Update Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Report Management Component
function ReportManagement({ token, user }) {
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState("daily");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7) // "YYYY-MM"
  );

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const generateReport = async () => {
    console.log(selectedMonth);
    const monthNumber = String(MONTHS.indexOf(selectedMonth) + 1).padStart(
      2,
      "0"
    );
    console.log(monthNumber);
    setIsLoading(true);
    try {
      const reportRequest = {
        report_type: reportType,
        ...(reportType === "custom" && {
          start_date: customStartDate,
          end_date: customEndDate,
        }),
        ...(reportType === "daily" && { date: selectedDate }),
        ...(paymentMethodFilter !== "all" && {
          payment_method: paymentMethodFilter,
        }),
      };

      // Add this block for monthly reports:
      if (reportType === "monthly") {
        const [year, month] = selectedMonth.split("-");
        // First day of month
        const start_date = new Date(
          year,
          month - 1,
          1,
          0,
          0,
          0,
          0
        ).toISOString();
        // Last day of month, 23:59:59.999
        const end_date = new Date(
          year,
          month,
          0,
          23,
          59,
          59,
          999
        ).toISOString();
        reportRequest.start_date = start_date;
        reportRequest.end_date = end_date;
      }

      if (reportType === "monthly") {
        const [year, month] = selectedMonth.split("-");
        const start_date = new Date(
          Date.UTC(year, month - 1, 1, 0, 0, 0, 0)
        ).toISOString();
        const end_date = new Date(
          Date.UTC(year, month, 0, 23, 59, 59, 999)
        ).toISOString();
        reportRequest.start_date = start_date;
        reportRequest.end_date = end_date;
      }

      const response = await fetch(`${BACKEND_URL}/api/reports/sales`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(reportRequest),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("🔍 Report data received:", data);
        console.log("🔍 Transactions count:", data.transactions?.length || 0);
        setReportData(data);
      } else {
        const error = await response.json();
        console.error("🔍 Report generation failed:", error);
        alert(error.detail || "Failed to generate report");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Error generating report");
    } finally {
      setIsLoading(false);
    }
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const reportRequest = {
        report_type: reportType,
        ...(reportType === "monthly" && {
          month: selectedMonth,
          year: selectedYear,
        }),
        ...(reportType === "daily" && { date: selectedDate }),

        ...(reportType === "custom" && {
          start_date: customStartDate,
          end_date: customEndDate,
        }),
        ...(paymentMethodFilter !== "all" && {
          payment_method: paymentMethodFilter,
        }),
      };

      const response = await fetch(`${BACKEND_URL}/api/reports/sales/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(reportRequest),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get("content-disposition");
        const filename = contentDisposition
          ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
          : "sales_report.xlsx";
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to export report");
      }
    } catch (error) {
      console.error("Error exporting report:", error);
      alert("Error exporting report");
    } finally {
      setIsExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return "₱0.00";
    }
    return `₱${amount.toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDateTime = (isoString) => {
    return new Date(isoString).toLocaleString("en-PH");
  };

  useEffect(() => {
    if (reportType === "monthly" && selectedMonth && selectedYear) {
      generateReport();
    }
  }, [reportType, selectedMonth, selectedYear, paymentMethodFilter, token]);

  // Auto-generate report when component mounts or report type changes
  useEffect(() => {
    if (reportType !== "custom" || (customStartDate && customEndDate)) {
      generateReport();
    }
  }, [reportType, selectedDate, paymentMethodFilter, token]);


  // Generate report for custom date range when both dates are selected
  useEffect(() => {
    if (reportType === "custom" && customStartDate && customEndDate) {
      generateReport();
    }
  }, [customStartDate, customEndDate, paymentMethodFilter, token]);

  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="p-6 bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="flex items-center space-x-2 text-2xl font-bold">
              <BarChart3 className="h-7 w-7 text-cwc-goldDark" />
              <span>Sales Reports</span>
            </h2>
            <p className="text-gray-600">
              Generate and export comprehensive sales reports
            </p>
          </div>
          <div className="flex space-x-2">
            {reportData && (
              <Button
                onClick={exportToExcel}
                disabled={isExporting}
                className="bg-cwc-gold hover:bg-cwc-goldDark text-cwc-black"
              >
                {isExporting ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-b-2 border-white rounded-full animate-spin"></div>
                    <span>Exporting...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <FileSpreadsheet className="w-4 h-4" />
                    <span>Export to Excel</span>
                  </div>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Report Type Selection */}
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-5">
          <div className="space-y-2">
            <Label>Report Type</Label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily Report</SelectItem>
                <SelectItem value="weekly">Weekly Report</SelectItem>
                <SelectItem value="monthly">Monthly Report</SelectItem>
                <SelectItem value="custom">Custom Date Range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Payment Method</Label>
            <Select
              value={paymentMethodFilter}
              onValueChange={setPaymentMethodFilter}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payment Methods</SelectItem>
                <SelectItem value="cash">💵 Cash Only</SelectItem>
                <SelectItem value="card">💳 Card Only</SelectItem>
                <SelectItem value="online">🌐 Online Payment Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {reportType === "daily" && (
            <div className="space-y-2">
              <Label>Select Date</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
          )}

          {reportType === "monthly" && (
            <div className="space-y-2">
              <Label>Select Month & Year</Label>
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-2 py-1 border rounded"
              />
            </div>
          )}

          {reportType === "custom" && (
            <>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="flex items-end">
            <Button
              onClick={generateReport}
              disabled={isLoading}
              className="w-full bg-cwc-gold hover:bg-cwc-goldDark text-cwc-black"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-b-2 border-white rounded-full animate-spin"></div>
                  <span>Generating...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <BarChart3 className="w-4 h-4" />
                  <span>Generate Report</span>
                </div>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Card className="border-l-4 border-l-cwc-gold">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Total Sales
                    </p>
                    <p className="text-2xl font-bold text-cwc-goldDark">
                      {formatCurrency(reportData.total_sales)}
                    </p>
                  </div>
                  <Banknote className="w-8 h-8 text-cwc-goldDark" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-cwc-gold">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Transactions
                    </p>
                    <p className="text-2xl font-bold text-cwc-goldDark">
                      {reportData.total_transactions}
                    </p>
                  </div>
                  <CreditCard className="w-8 h-8 text-cwc-goldDark" />
                </div>
              </CardContent>
            </Card>

            {/* <Card className="border-l-4 border-l-cwc-gold">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Items Sold
                    </p>
                    <p className="text-2xl font-bold text-cwc-goldDark">
                      {reportData.total_items_sold}
                    </p>
                  </div>
                  <Package className="w-8 h-8 text-cwc-goldDark" />
                </div>
              </CardContent>
            </Card> */}

            <Card className="border-l-4 border-l-cwc-gold">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      Avg. Transaction
                    </p>
                    <p className="text-2xl font-bold text-cwc-goldDark">
                      {formatCurrency(reportData.average_transaction)}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-cwc-goldDark" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Period Info */}
          <Card className="shadow-xl">
            <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-cwc-black to-cwc-gold">
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="w-5 h-5" />
                <span>Report Period: {reportData.period}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {/* Payment Methods */}
                <div>
                  <h3 className="mb-3 text-lg font-semibold text-gray-800">
                    Payment Methods
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">💵 Cash:</span>
                      <span className="font-medium">
                        {formatCurrency(reportData.cash_sales)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">💳 Card:</span>
                      <span className="font-medium">
                        {formatCurrency(reportData.card_sales)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">🌐 Online Payment:</span>
                      <span className="font-medium">
                        {formatCurrency(reportData.online_sales)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Customer Types */}
                <div>
                  <h3 className="mb-3 text-lg font-semibold text-gray-800">
                    Customer Types
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">👥 Members:</span>
                      <span className="font-medium">
                        {formatCurrency(reportData.member_sales)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">🚶 Walk-ins:</span>
                      <span className="font-medium">
                        {formatCurrency(reportData.non_member_sales)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Financial Summary */}
                <div>
                  <h3 className="mb-3 text-lg font-semibold text-gray-800">
                    Financial Summary
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">💸 Discounts:</span>
                      <span className="font-medium text-green-600">
                        {formatCurrency(reportData.total_discounts)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Products */}
          {reportData.top_products && reportData.top_products.length > 0 && (
            <Card className="shadow-xl">
              <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-cwc-black to-cwc-gold">
                <CardTitle className="flex items-center space-x-2">
                  <Award className="w-5 h-5" />
                  <span>Top Selling Services</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {reportData.top_products.map((product, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 rounded-lg bg-gray-50"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 font-bold rounded-full text-cwc-goldDark bg-cwc-gold/20">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-800">
                            {product.name}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {product.quantity} services sold
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-cwc-goldDark">
                          {formatCurrency(product.revenue)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filtered Transactions */}
          {reportData.transactions && reportData.transactions.length > 0 && (
            <Card className="shadow-xl">
              <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-cwc-black to-cwc-gold">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="w-5 h-5" />
                    <span>
                      Filtered Transactions ({reportData.transactions.length})
                    </span>
                  </div>
                  <div className="text-sm font-normal opacity-90">
                    {reportType.charAt(0).toUpperCase() + reportType.slice(1)}
                    {paymentMethodFilter !== "all" &&
                      ` • ${
                        paymentMethodFilter.charAt(0).toUpperCase() +
                        paymentMethodFilter.slice(1)
                      } only`}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {/* Filter Summary */}
                <div className="p-3 mb-4 border-l-4 rounded-lg border-cwc-gold bg-cwc-gold/10">
                  <div className="flex items-center space-x-4 text-sm">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4 text-cwc-goldDark" />
                      <span className="font-medium">Period:</span>
                      <span>{reportData.period}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <CreditCard className="w-4 h-4 text-cwc-goldDark" />
                      <span className="font-medium">Payment:</span>
                      <span>
                        {paymentMethodFilter === "all"
                          ? "All Methods"
                          : paymentMethodFilter.charAt(0).toUpperCase() +
                            paymentMethodFilter.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <BarChart3 className="w-4 h-4 text-cwc-goldDark" />
                      <span className="font-medium">Total:</span>
                      <span className="font-bold text-cwc-goldDark">
                        {formatCurrency(reportData.total_sales)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="px-3 py-2 text-left">Transaction ID</th>
                        <th className="px-3 py-2 text-left">Date & Time</th>
                        <th className="px-3 py-2 text-left">Customer</th>
                        <th className="px-3 py-2 text-left">Payment Method</th>
                        <th className="px-3 py-2 text-left">Payment Details</th>
                        <th className="px-3 py-2 text-left">Quantity</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                        <th className="px-3 py-2 text-right">Discount</th>
                        <th className="px-3 py-2 text-left">Cashier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.transactions.map((transaction, index) => (
                        <tr
                          key={transaction.id || index}
                          className="border-b hover:bg-gray-50"
                        >
                          <td className="px-3 py-2 font-mono text-xs">
                            #
                            {transaction.id.split("_")[1] ||
                              transaction.id.substr(-8)}
                          </td>
                          <td className="px-3 py-2">
                            {formatDateTime(transaction.timestamp)}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={
                                transaction.member_name === "Walk-in"
                                  ? "text-gray-500"
                                  : "text-cwc-goldDark"
                              }
                            >
                              {transaction.member_name}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <Badge
                              variant="outline"
                              className={`text-xs border-cwc-gold text-cwc-goldDark`}
                            >
                              {transaction.payment_method === "cash"
                                ? "💵 Cash"
                                : transaction.payment_method === "card"
                                ? "💳 Card"
                                : transaction.payment_method === "online"
                                ? "🌐 Online Payment"
                                : transaction.payment_method}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">
                            {transaction.payment_method === "cash" && (
                              <div>Cash Onsite</div>
                            )}
                            {transaction.payment_method === "card" && (
                              <div className="space-y-1">
                                {transaction.card_number && (
                                  <div>Card: {transaction.card_number}</div>
                                )}
                                {transaction.transaction_number && (
                                  <div>
                                    Txn: {transaction.transaction_number}
                                  </div>
                                )}
                              </div>
                            )}
                            {transaction.payment_method === "online" && (
                              <div className="space-y-1">
                                {transaction.payment_gateway && (
                                  <div>
                                    Gateway: {transaction.payment_gateway}
                                  </div>
                                )}
                                {transaction.account_number && (
                                  <div>
                                    Account: {transaction.account_number}
                                  </div>
                                )}
                                {transaction.transaction_number && (
                                  <div>
                                    Ref: {transaction.transaction_number}
                                  </div>
                                )}
                              </div>
                            )}
                            {transaction.payment_method === "cash" && (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {transaction.items_count}
                          </td>
                          <td className="px-3 py-2 font-medium text-right">
                            {formatCurrency(transaction.total_amount)}
                          </td>
                          <td className="px-3 py-2 text-right text-cwc-goldDark">
                            {formatCurrency(transaction.discount_amount)}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600">
                            {transaction.cashier}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {reportData.transactions.length === 0 && (
                    <div className="py-8 text-center text-gray-500">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="text-lg font-medium">
                        No transactions found
                      </p>
                      <p className="text-sm">
                        Try adjusting your filters or date range
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Transactions State */}
          {reportData &&
            (!reportData.transactions ||
              reportData.transactions.length === 0) && (
              <Card className="shadow-xl">
                <CardContent className="p-12 text-center">
                  <CreditCard className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="mb-2 text-xl font-semibold text-gray-600">
                    No Transactions Found
                  </h3>
                  <p className="mb-4 text-gray-500">
                    No transactions match your current filters:
                  </p>
                  <div className="inline-flex items-center px-4 py-2 space-x-4 text-sm text-gray-600 bg-gray-100 rounded-lg">
                    <span>📅 {reportData.period}</span>
                    <span>•</span>
                    <span>
                      💳{" "}
                      {paymentMethodFilter === "all"
                        ? "All Payment Methods"
                        : paymentMethodFilter.charAt(0).toUpperCase() +
                          paymentMethodFilter.slice(1)}
                    </span>
                  </div>
                  <p className="mt-4 text-sm text-gray-500">
                    Try selecting a different date range or payment method
                    filter.
                  </p>
                </CardContent>
              </Card>
            )}
        </div>
      )}

      {/* No Data State */}
      {!reportData && !isLoading && (
        <Card className="shadow-xl">
          <CardContent className="p-12 text-center">
            <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="mb-2 text-xl font-semibold text-gray-600">
              No Report Generated
            </h3>
            <p className="text-gray-500">
              Select report parameters and click "Generate Report" to view sales
              data.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Membership Type Management Component
function MembershipTypeManagement({ token, user }) {
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [typeForm, setTypeForm] = useState({
    name: "",
    discount_percentage: "",
    description: "",
    benefits: [],
  });

  const fetchMembershipTypes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/membership-types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setMembershipTypes(data);
    } catch (error) {
      console.error("Error fetching membership types:", error);
    }
  };

  const handleCreateMembershipType = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/membership-types`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...typeForm,
          discount_percentage: parseFloat(typeForm.discount_percentage),
          benefits: typeForm.benefits || [],
        }),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setTypeForm({
          name: "",
          discount_percentage: "",
          description: "",
          benefits: [],
        });
        fetchMembershipTypes();
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to create membership type");
      }
    } catch (error) {
      console.error("Error creating membership type:", error);
      alert("Error creating membership type");
    }
  };

  const handleUpdateMembershipType = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...typeForm,
        discount_percentage: typeForm.discount_percentage
          ? parseFloat(typeForm.discount_percentage)
          : undefined,
      };

      const response = await fetch(
        `${BACKEND_URL}/api/membership-types/${selectedType.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedType(null);
        fetchMembershipTypes();
      } else {
        const error = await response.json();
        alert(error.detail || "Failed to update membership type");
      }
    } catch (error) {
      console.error("Error updating membership type:", error);
      alert("Error updating membership type");
    }
  };

  const handleDeleteMembershipType = async (typeId, typeName) => {
    if (user?.role !== "admin") {
      alert("Access denied. Only admin users can delete membership types.");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete "${typeName}" membership type? This action cannot be undone.`
      )
    ) {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/membership-types/${typeId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.ok) {
          fetchMembershipTypes();
        } else {
          const error = await response.json();
          alert(error.detail || "Failed to delete membership type");
        }
      } catch (error) {
        console.error("Error deleting membership type:", error);
        alert("Error deleting membership type");
      }
    }
  };

  const addBenefit = () => {
    setTypeForm({
      ...typeForm,
      benefits: [...(typeForm.benefits || []), ""],
    });
  };

  const updateBenefit = (index, value) => {
    const newBenefits = [...(typeForm.benefits || [])];
    newBenefits[index] = value;
    setTypeForm({
      ...typeForm,
      benefits: newBenefits,
    });
  };

  const removeBenefit = (index) => {
    const newBenefits = [...(typeForm.benefits || [])];
    newBenefits.splice(index, 1);
    setTypeForm({
      ...typeForm,
      benefits: newBenefits,
    });
  };

  const getMembershipIcon = (name) => {
    const lowercaseName = name.toLowerCase();
    if (lowercaseName.includes("vip") || lowercaseName.includes("platinum"))
      return Crown;
    if (lowercaseName.includes("premium") || lowercaseName.includes("gold"))
      return Award;
    if (lowercaseName.includes("standard") || lowercaseName.includes("silver"))
      return Star;
    return Users;
  };

  const getMembershipColor = (name) => {
    const lowercaseName = name.toLowerCase();
    if (lowercaseName.includes("vip") || lowercaseName.includes("platinum"))
      return "text-purple-600 bg-purple-100";
    if (lowercaseName.includes("premium") || lowercaseName.includes("gold"))
      return "text-yellow-600 bg-yellow-100";
    if (lowercaseName.includes("standard") || lowercaseName.includes("silver"))
      return "text-blue-600 bg-blue-100";
    return "text-gray-600 bg-gray-100";
  };

  useEffect(() => {
    fetchMembershipTypes();
  }, []);

  // Only show to admin users
  if (user?.role !== "admin") {
    return (
      <div className="py-12 text-center">
        <Crown className="w-16 h-16 mx-auto mb-4 text-gray-400" />
        <h2 className="mb-2 text-xl font-semibold text-gray-600">
          Access Restricted
        </h2>
        <p className="text-gray-500">
          Only admin users can manage membership types.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Membership Type Management</h2>
          <p className="text-gray-600">
            Configure membership tiers and benefits
          </p>
        </div>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="text-black bg-cwc-gold hover:bg-cwc-goldDark"
        >
          <Crown className="w-4 h-4 mr-2" />
          Add Membership Type
        </Button>
      </div>

      {/* Membership Types Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {membershipTypes.map((type) => {
          const IconComponent = getMembershipIcon(type.name);
          const colorClass = getMembershipColor(type.name);

          return (
            <Card
              key={type.id}
              className="transition-shadow border-l-4 hover:shadow-lg border-l-cwc-gold"
            >
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded bg-gray-50">
                        <IconComponent className="w-6 h-6 text-gray-700" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">
                          {type.name}
                        </h3>
                        <Badge
                          variant="outline"
                          className="text-cwc-goldDark border-cwc-gold"
                        >
                          {type.discount_percentage}% discount
                        </Badge>
                      </div>
                    </div>
                    <Badge
                      className={
                        type.is_active
                          ? "bg-cwc-gold text-cwc-black hover:bg-cwc-gold hover:!text-cwc-black cursor-default pointer-events-none select-none"
                          : "bg-gray-200 text-gray-700 cursor-default pointer-events-none select-none"
                      }
                    >
                      {type.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>

                  <p className="text-sm text-gray-600">{type.description}</p>

                  {type.benefits && type.benefits.length > 0 && (
                    <div>
                      <h4 className="mb-2 text-sm font-medium text-gray-700">
                        Benefits:
                      </h4>
                      <ul className="space-y-1 text-xs text-gray-600">
                        {type.benefits.slice(0, 3).map((benefit, index) => (
                          <li
                            key={index}
                            className="flex items-center space-x-2"
                          >
                            <Check className="flex-shrink-0 w-3 h-3 text-cwc-goldDark" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                        {type.benefits.length > 3 && (
                          <li className="text-gray-500">
                            +{type.benefits.length - 3} more benefits
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedType(type);
                        setTypeForm({
                          name: type.name,
                          discount_percentage:
                            type.discount_percentage.toString(),
                          description: type.description || "",
                          benefits: type.benefits || [],
                        });
                        setIsEditDialogOpen(true);
                      }}
                      className="flex-1"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        handleDeleteMembershipType(type.id, type.name)
                      }
                      className="border-cwc-gold text-cwc-gold hover:bg-cwc-gold hover:text-cwc-black"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create Membership Type Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Membership Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateMembershipType} className="space-y-4">
            <div>
              <Label htmlFor="name">Membership Type Name</Label>
              <Input
                id="name"
                value={typeForm.name}
                onChange={(e) =>
                  setTypeForm({ ...typeForm, name: e.target.value })
                }
                required
                placeholder="e.g., Premium, VIP, Gold"
              />
            </div>
            <div>
              <Label htmlFor="discount_percentage">
                Discount Percentage (%)
              </Label>
              <Input
                id="discount_percentage"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={typeForm.discount_percentage}
                onChange={(e) =>
                  setTypeForm({
                    ...typeForm,
                    discount_percentage: e.target.value,
                  })
                }
                required
                placeholder="e.g., 15"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={typeForm.description}
                onChange={(e) =>
                  setTypeForm({ ...typeForm, description: e.target.value })
                }
                rows={3}
                placeholder="Describe this membership type..."
              />
            </div>
            <div>
              <Label>Benefits</Label>
              <div className="space-y-2">
                {(typeForm.benefits || []).map((benefit, index) => (
                  <div key={index} className="flex space-x-2">
                    <Input
                      value={benefit}
                      onChange={(e) => updateBenefit(index, e.target.value)}
                      placeholder="Enter benefit..."
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeBenefit(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addBenefit}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Benefit
                </Button>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="text-black bg-cwc-gold hover:bg-cwc-goldDark"
              >
                Create Membership Type
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Membership Type Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Membership Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateMembershipType} className="space-y-4">
            <div>
              <Label htmlFor="edit_name">Membership Type Name</Label>
              <Input
                id="edit_name"
                value={typeForm.name}
                onChange={(e) =>
                  setTypeForm({ ...typeForm, name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_discount_percentage">
                Discount Percentage (%)
              </Label>
              <Input
                id="edit_discount_percentage"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={typeForm.discount_percentage}
                onChange={(e) =>
                  setTypeForm({
                    ...typeForm,
                    discount_percentage: e.target.value,
                  })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_description">Description</Label>
              <Textarea
                id="edit_description"
                value={typeForm.description}
                onChange={(e) =>
                  setTypeForm({ ...typeForm, description: e.target.value })
                }
                rows={3}
              />
            </div>
            <div>
              <Label>Benefits</Label>
              <div className="space-y-2">
                {(typeForm.benefits || []).map((benefit, index) => (
                  <div key={index} className="flex space-x-2">
                    <Input
                      value={benefit}
                      onChange={(e) => updateBenefit(index, e.target.value)}
                      placeholder="Enter benefit..."
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeBenefit(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addBenefit}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Benefit
                </Button>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="text-black bg-cwc-gold hover:bg-cwc-goldDark"
              >
                Update Membership Type
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Member Management Component
function MemberManagement({ token, user }) {
  const [members, setMembers] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [memberForm, setMemberForm] = useState({
    name: "",
    email: "",
    phone: "",
    membership_type: "",
    discount_percentage: 0,
    address: "",
    notes: "",
    date_joined: "", // <-- add this line
  });

  const fetchMembershipTypes = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/membership-types/active`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      setMembershipTypes(data);
    } catch (error) {
      console.error("Error fetching membership types:", error);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setMembers(data);
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/members`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(memberForm),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setMemberForm({
          name: "",
          email: "",
          phone: "",
          membership_type: "",
          address: "",
          notes: "",
        });
        fetchMembers();
      }
    } catch (error) {
      console.error("Error creating member:", error);
    }
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/members/${selectedMember.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(memberForm),
        }
      );

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedMember(null);
        fetchMembers();
      }
    } catch (error) {
      console.error("Error updating member:", error);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (user?.role !== "admin") {
      alert("Access denied. Only admin users can delete members.");
      return;
    }

    if (window.confirm("Are you sure you want to delete this member?")) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/members/${memberId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          fetchMembers();
        } else {
          const error = await response.json();
          alert(error.detail || "Failed to delete member");
        }
      } catch (error) {
        console.error("Error deleting member:", error);
        alert("Error deleting member");
      }
    }
  };

  const handleViewDetails = async (member) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/members/${member.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const memberDetails = await response.json();
      setSelectedMember(memberDetails);
      setIsDetailsDialogOpen(true);
    } catch (error) {
      console.error("Error fetching member details:", error);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchMembershipTypes();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Member Management</h2>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="text-black bg-cwc-gold hover:bg-cwc-goldDark"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Member
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {members.map((member) => (
          <Card key={member.id} className="transition-shadow hover:shadow-lg">
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <h3 className="font-semibold text-gray-800">{member.name}</h3>
                  <Badge
                    className={
                      member.status === "active"
                        ? "bg-cwc-gold text-cwc-black hover:bg-cwc-gold hover:!text-cwc-black cursor-default pointer-events-none select-none"
                        : "bg-gray-200 text-gray-700 cursor-default pointer-events-none select-none"
                    }
                  >
                    {member.status}
                  </Badge>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <p>{member.email}</p>
                  <p>{member.phone}</p>
                   <p>Date Joined: {member.date_joined ? new Date(member.date_joined).toLocaleDateString() : "N/A"}</p>
                </div>
                <div className="flex items-center justify-between">
                  <Badge
                    variant="outline"
                    className="text-cwc-black border-cwc-gold"
                  >
                    {member.membership_type}
                  </Badge>
                  <Badge
                    variant="outline"
                    className="text-cwc-goldDark border-cwc-gold"
                  >
                    {member.discount_percentage}% off
                  </Badge>
                </div>
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewDetails(member)}
                    className="flex-1"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedMember(member);
                      setMemberForm(member);
                      setIsEditDialogOpen(true);
                    }}
                    className="flex-1"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  {user?.role === "admin" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteMember(member.id)}
                      className="border-cwc-gold text-cwc-gold hover:bg-cwc-gold hover:text-cwc-black"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Member Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateMember} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={memberForm.name}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={memberForm.email}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, email: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={memberForm.phone}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, phone: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="membership_type">Membership Type</Label>
              <Select
                value={memberForm.membership_type}
                onValueChange={(value) => {
                  setMemberForm({
                    ...memberForm,
                    membership_type: value,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select membership type" />
                </SelectTrigger>
                <SelectContent>
                  {membershipTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.name} ({type.discount_percentage}% discount)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={memberForm.address}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, address: e.target.value })
                }
              />
            </div>
            <div>
            <Label htmlFor="date_joined">Date Joined</Label>
            <Input
              id="date_joined"
              type="date"
              value={memberForm.date_joined}
              onChange={(e) =>
                setMemberForm({ ...memberForm, date_joined: e.target.value })
              }
              required
            />
          </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={memberForm.notes}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, notes: e.target.value })
                }
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="text-black bg-cwc-gold hover:bg-cwc-goldDark"
              >
                Create Member
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateMember} className="space-y-4">
            <div>
              <Label htmlFor="edit_name">Full Name</Label>
              <Input
                id="edit_name"
                value={memberForm.name}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, name: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={memberForm.email}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, email: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_phone">Phone</Label>
              <Input
                id="edit_phone"
                value={memberForm.phone}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, phone: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_membership_type">Membership Type</Label>
              <Select
                value={memberForm.membership_type}
                onValueChange={(value) => {
                  setMemberForm({
                    ...memberForm,
                    membership_type: value,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {membershipTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.name} ({type.discount_percentage}% discount)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_status">Status</Label>
              <Select
                value={memberForm.status}
                onValueChange={(value) =>
                  setMemberForm({ ...memberForm, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_address">Address</Label>
              <Input
                id="edit_address"
                value={memberForm.address}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, address: e.target.value })
                }
              />
            </div>
            <div>
              <Label htmlFor="edit_date_joined">Date Joined</Label>
              <Input
                id="edit_date_joined"
                type="date"
                value={memberForm.date_joined}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, date_joined: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_notes">Notes</Label>
              <Textarea
                id="edit_notes"
                value={memberForm.notes}
                onChange={(e) =>
                  setMemberForm({ ...memberForm, notes: e.target.value })
                }
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="text-black bg-cwc-gold hover:bg-cwc-goldDark"
              >
                Update Member
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Member Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Member Details</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">
                    Name
                  </Label>
                  <p className="text-lg font-semibold">{selectedMember.name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">
                    Status
                  </Label>
                  <Badge
                    className={
                      selectedMember.status === "active"
                        ? "bg-cwc-gold text-cwc-black hover:bg-cwc-gold hover:!text-cwc-black cursor-default pointer-events-none select-none"
                        : "bg-gray-200 text-gray-700 cursor-default pointer-events-none select-none"
                    }
                  >
                    {selectedMember.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">
                    Email
                  </Label>
                  <p>{selectedMember.email}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">
                    Phone
                  </Label>
                  <p>{selectedMember.phone}</p>
                </div>
                 <div>
                  <Label className="text-sm font-medium text-gray-500">Date Joined</Label>
                  <p>{selectedMember.date_joined ? new Date(selectedMember.date_joined).toLocaleDateString() : "N/A"}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">
                    Membership Type
                  </Label>
                  <Badge
                    variant="outline"
                    className="text-cwc-black border-cwc-gold"
                  >
                    {selectedMember.membership_type}
                  </Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">
                    Discount
                  </Label>
                  <Badge
                    variant="outline"
                    className="text-cwc-goldDark border-cwc-gold"
                  >
                    {selectedMember.discount_percentage}% off
                  </Badge>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium text-gray-500">
                    Address
                  </Label>
                  <p>{selectedMember.address || "Not provided"}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium text-gray-500">
                    Notes
                  </Label>
                  <p>{selectedMember.notes || "No notes"}</p>
                </div>
              </div>

              {selectedMember.transaction_history &&
                selectedMember.transaction_history.length > 0 && (
                  <div>
                    <Label className="mb-3 text-lg font-semibold">
                      Recent Transactions
                    </Label>
                    <div className="space-y-2 overflow-y-auto max-h-60">
                      {selectedMember.transaction_history.map((transaction) => (
                        <Card key={transaction.id} className="p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">
                                ₱{transaction.total_amount.toFixed(2)}
                              </p>
                              <p className="text-sm text-gray-500">
                                {new Date(
                                  transaction.timestamp
                                ).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge
                              variant="outline"
                              className="border-cwc-gold text-cwc-black"
                            >
                              {transaction.payment_method}
                            </Badge>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [products, setProducts] = useState([]);
  const [members, setMembers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [cardNumber, setCardNumber] = useState("");
  const [cardValidator, setCardValidator] = useState(false);
  const formatCardNumber = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "").slice(0, 16); // Limit to 16 digits
    // Group into 4s and join with spaces
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };
  const [transactionNumber, setTransactionNumber] = useState("");
  const [orNumber, setOrNumber] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [selectedBank, setSelectedBank] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [activeTab, setActiveTab] = useState("pos");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isCartSummaryModalOpen, setIsCartSummaryModalOpen] = useState(false);
  const [memberFieldTouched, setMemberFieldTouched] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const { toast } = useToast();
  const memberSearchInputRef = React.useRef(null);
  const walkinInputRef = React.useRef(null);

  // Only show categories that actually exist in current products, preserving fixed order
  const availablePosCategories = products.map((product) => product.category).reduce(
  (accumulator, currentValue) => accumulator.includes(currentValue) ? accumulator : [...accumulator, currentValue],
  [],
);

  const onlinePaymentBanks = [
    {
      value: "gcash",
      label: "GCash",
      logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/57/GCash-logo.svg/1200px-GCash-logo.svg.png",
    },
    {
      value: "maya",
      label: "Maya",
      logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4c/Maya_logo.svg/1200px-Maya_logo.svg.png",
    },
    {
      value: "bdo",
      label: "BDO",
      logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/68/BDO_Logo.svg/1200px-BDO_Logo.svg.png",
    },
    {
      value: "unionbank",
      label: "UnionBank",
      logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5b/UnionBank_logo.svg/1200px-UnionBank_logo.svg.png",
    },
  ];

  // Check for existing login on app start
  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
  }, []);

  // Fetch data functions
  const fetchWithAuth = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      // Avoid returning cached GET responses
      cache: "no-store",
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        // Best-effort cache prevention
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    });

    if (response.status === 401) {
      // Clear token and user state
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setToken(null);
      setUser(null);

      // Clear any sensitive data
      setCart([]);
      setSelectedMember(null);
      setMemberSearch("");
      setMemberSearchResults([]);
      setPaymentStatus("");

      // Redirect to login page
      window.location.href = "/";
      return null;
    }

    return response;
  };

  const fetchProducts = async () => {
    if (!token) return;
    try {
      const cacheBuster = Date.now();
      const response = await fetchWithAuth(
        `${BACKEND_URL}/api/active-products?t=${cacheBuster}`
      );
      if (response && response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchMembers = async () => {
    if (!token) return;
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/members`);
      if (response && response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const fetchTransactions = async () => {
    if (!token) return;
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/transactions`);
      if (response && response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  // const fetchLowStockItems = async () => {
  //   if (!token) return;
  //   try {
  //     const response = await fetchWithAuth(
  //       `${BACKEND_URL}/api/packages/low-stock`
  //     );
  //     if (response && response.ok) {
  //       const data = await response.json();
  //       setLowStockItems(data);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching low stock items:", error);
  //   }
  // };

  const searchMembers = async (query) => {
    if (!query.trim() || !token) {
      setMemberSearchResults([]);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${BACKEND_URL}/api/members/search?query=${encodeURIComponent(query)}`
      );
      if (response && response.ok) {
        const data = await response.json();
        setMemberSearchResults(data);
      }
    } catch (error) {
      console.error("Error searching members:", error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchProducts();
      fetchMembers();
      fetchTransactions();
      // fetchLowStockItems();
    }
  }, [token]);

  // Ensure selectedCategory remains valid based on available POS categories
  useEffect(() => {
    if (
      selectedCategory !== "all" &&
      !availablePosCategories.includes(selectedCategory)
    ) {
      setSelectedCategory("all");
    }
  }, [products, availablePosCategories]);

  // Listen for product updates coming from other components (e.g., Inventory/ProductManagement)
  useEffect(() => {
    if (!token) return;
    const onProductsUpdated = () => {
      fetchProducts();
      // fetchLowStockItems();
    };
    window.addEventListener("products-updated", onProductsUpdated);
    return () => window.removeEventListener("products-updated", onProductsUpdated);
  }, [token]);

  // When user switches to POS tab, ensure products are fresh
  useEffect(() => {
    if (!token) return;
    if (activeTab === "pos") {
      fetchProducts();
      // fetchLowStockItems();
    }
  }, [activeTab, token]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      searchMembers(memberSearch);
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [memberSearch, token]);

  const handleLogin = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
  };

  const handleLogout = () => {
    // Clear token and user state
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);

    // Clear any sensitive data
    setCart([]);
    setSelectedMember(null);
    setMemberSearch("");
    setMemberSearchResults([]);
    setPaymentStatus("");

    // Redirect to login page
    window.location.href = "/";
  };

  // Cart functions
  const addToCart = (product) => {
    // enforce stock cap
    const stock = product?.stock_quantity ?? Infinity;
    if (stock <= 0) {
      toast({
        title: "Out of stock",
        description: `${product.name} is currently unavailable.`,
      });
      return;
    }
    setCart((prevCart) => {
      const existingItem = prevCart.find(
        (item) => item.product_id === product.id
      );
      if (existingItem) {
        const nextQty = Math.min(existingItem.quantity + 1, stock);
        // if (nextQty === existingItem.quantity) {
        //   toast({
        //     title: "Stock limit reached",
        //     description: `Only ${stock} in stock for ${product.name}.`,
        //   });
        //   return prevCart;
        // }
        return prevCart.map((item) =>
          item.product_id === product.id
            ? { ...item, quantity: nextQty }
            : item
        );
      } else {
        return [
          ...prevCart,
          {
            product_id: product.id,
            product_name: product.name,
            price: product.price,
            quantity: 1,
          },
        ];
      }
    });
  };

  const updateCartItemQuantity = (productId, newQuantity) => {
    const product = products.find((p) => p.id === productId);
    const stock = product?.stock_quantity ?? Infinity;
    // if (newQuantity <= 0) {
    //   setCart((prevCart) =>
    //     prevCart.filter((item) => item.product_id !== productId)
    //   );
    //   return;
    // }
    const clamped = Math.min(newQuantity, stock);
    // if (newQuantity > stock) {
    //   toast({
    //     title: "Stock limit reached",
    //     description: `Only ${stock} in stock for ${product?.name || "item"}.`,
    //   });
    // }
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.product_id === productId ? { ...item, quantity: clamped } : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart((prevCart) =>
      prevCart.filter((item) => item.product_id !== productId)
    );
  };

  const clearCart = () => {
    setCart([]);
    setSelectedMember(null);
    setMemberSearch("");
    setMemberSearchResults([]);
    setPaymentStatus("");
    setOrNumber("");
  };

  // Calculate totals
  const subtotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const discountAmount = selectedMember
    ? (subtotal * selectedMember.discount_percentage) / 100
    : 0;
  const total = subtotal - discountAmount;

  // Payment functions
  const processPayment = async () => {
    console.log("🚀 processPayment called");

    if (cart.length === 0) {
      alert("Please add items to cart before checkout.");
      return;
    }

    // Mandatory member selection validation
    if (!selectedMember) {
      alert(
        "Please select a member before completing the purchase. Member selection is required for all transactions."
      );
      return;
    }

    // Mandatory OR number validation (pre-printed booklet)
    if (!orNumber || !orNumber.trim()) {
      alert("Please enter the Official Receipt (OR) number.");
      return;
    }

    console.log("🚀 Starting payment process with:", {
      cart: cart.length + " items",
      member: selectedMember.name,
      paymentMethod: paymentMethod,
      total: total.toFixed(2),
    });

    setIsProcessingPayment(true);
    setPaymentStatus("");

    try {
      // Create transaction
      const transactionId = `txn_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const transaction = {
        id: transactionId,
        items: cart,
        subtotal: parseFloat(subtotal.toFixed(2)),
        discount_amount: parseFloat(discountAmount.toFixed(2)),
        total_amount: parseFloat(total.toFixed(2)),
        payment_status: "Confirmed",
        payment_method: paymentMethod,
        payment_gateway: selectedBank,
        account_number: bankAccountNumber,
        card_number: cardNumber,
        transaction_number: transactionNumber,
        or_number: orNumber,
        member_id: selectedMember.id,
        member_name: selectedMember.name,
        timestamp: new Date().toISOString(),
      };
      const validateOR = transactions.find(item => item.or_number === orNumber);
      if (validateOR){
         toast({
          title: "Transaction Failed!",
          description: `OR number ${orNumber} has already been used. Please use a different OR number.`,
          variant: "destructive",
        });
        return;
      }
      else
      {
      console.log("🚀 Creating transaction:", transaction);
      // Put the most simple case
      if (paymentMethod === "cash") {
        setPaymentStatus(
          "✅ Payment completed successfully with cash! Transaction has been recorded."
        );
        clearCart();
        fetchProducts(); // Refresh packages
        fetchTransactions();
        setIsCartSummaryModalOpen(false);

        // Show success toast
        toast({
          title: "✅ Transaction Successful!",
          description: `Payment of ₱${total.toFixed(
            2
          )} completed successfully. Transaction has been recorded.`,
          variant: "default",
        });
        console.log("🚀 Cash payment completed successfully");
      }
      else if(paymentMethod === "card") {
        if(!cardNumber || !transactionNumber){
          toast({
                title: "Transaction Failed!",
                description: `Card number and transaction reference number are required for card payments.`,
                variant: "destructive",
              });
          throw new Error(
              "Card number and transaction reference number are required for card payments."
          );
        } 
        else
        {
          const validateCard = await fetch(`${BACKEND_URL}/api/payments/validate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ 
                    card_number: cardNumber
                })
              });
              const data = await validateCard.json();
              if(data.is_valid){
                console.log("🚀 Creating payment session for card payment");
                const transactionResponse = await fetchWithAuth(
            `${BACKEND_URL}/api/transactions`,
            {
              method: "POST",
              body: JSON.stringify(transaction),
            }
          );
          console.log("🚀 Transaction response:", transactionResponse);
          if (!transactionResponse || !transactionResponse.ok) {
            const errorData = await transactionResponse.json().catch(() => ({}));
            console.error("🚀 Transaction failed:", errorData);
            throw new Error(errorData.detail || "Failed to create transaction");
          }

          console.log("🚀 Transaction created successfully");
            setPaymentStatus(
              "✅ Card payment completed successfully! Transaction has been recorded."
            );
            // Clear payment inputs
            setCardNumber("");
            setTransactionNumber("");
            clearCart();
            fetchProducts(); // Refresh packages
            fetchTransactions();
            setIsCartSummaryModalOpen(false);

            // Show success toast
            toast({
              title: "✅ Transaction Successful!",
              description: `Payment of ₱${total.toFixed(
                2
              )} completed successfully. Transaction has been recorded.`,
              variant: "default",
            });

            console.log("🚀 Card payment completed successfully");
          } 
          else
          {
            throw new Error(
              "Sorry, your card is invalid. Put another card."
            );
          }
        }
      } 
      if(paymentMethod === "online" && (!selectedBank || !bankAccountNumber || !transactionNumber)) {
        toast({
              title: "Transaction Failed!",
              description: `Bank selection, account number, and transaction reference number are required for online payments.`,
              variant: "destructive",
            });
        throw new Error(
            "Bank selection, account number, and transaction reference number are required for online payments."
        );
      }


      const validateCard = await fetch(`${BACKEND_URL}/api/payments/validate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
              card_number: cardNumber
          })
        });
        const data = await validateCard.json();
        if(data.is_valid){
          console.log("🚀 Creating payment session for card payment");
          setPaymentStatus(
            "✅ Card payment completed successfully! Transaction has been recorded."
          );
          // Clear payment inputs
          setCardNumber("");
          setTransactionNumber("");
          clearCart();
          fetchProducts(); // Refresh packages
          fetchTransactions();
          setIsCartSummaryModalOpen(false);

          // Show success toast
          toast({
            title: "✅ Transaction Successful!",
            description: `Payment of ₱${total.toFixed(
              2
            )} completed successfully. Transaction has been recorded.`,
            variant: "default",
          });

          console.log("🚀 Card payment completed successfully");
        } 
      // Handle different payment methods
      if (paymentMethod === "cash") {
        const transactionResponse = await fetchWithAuth(
        `${BACKEND_URL}/api/transactions`,
        {
          method: "POST",
          body: JSON.stringify(transaction),
        }
      );
      console.log("🚀 Transaction response:", transactionResponse);
      if (!transactionResponse || !transactionResponse.ok) {
        const errorData = await transactionResponse.json().catch(() => ({}));
        console.error("🚀 Transaction failed:", errorData);
        throw new Error(errorData.detail || "Failed to create transaction");
      }

      console.log("🚀 Transaction created successfully");

        setPaymentStatus(
          "✅ Payment completed successfully with cash! Transaction has been recorded."
        );
        setSelectedBank("");
        setBankAccountNumber("");
        setTransactionNumber("");
        setCardNumber("");
        clearCart();
        fetchProducts(); // Refresh packages
        fetchTransactions();
        setIsCartSummaryModalOpen(false);

        // Show success toast
        toast({
          title: "✅ Transaction Successful!",
          description: `Payment of ₱${total.toFixed(
            2
          )} completed successfully. OR: ${orNumber}. Transaction has been recorded.`,
          variant: "default",
        });
        console.log("🚀 Cash payment completed successfully");
      } else if (paymentMethod === "online") {
        // For online payments, we need to validate bank selection, account number and transaction reference
        if (!selectedBank || !bankAccountNumber || !transactionNumber) {
          throw new Error(
            "Bank selection, account number, and transaction reference number are required for online payments"
          );
        }
        console.log("🚀 Creating payment session for online payment");
        const transactionResponse = await fetchWithAuth(
            `${BACKEND_URL}/api/transactions`,
            {
              method: "POST",
              body: JSON.stringify(transaction),
            }
          );
          
          


          console.log("🚀 Transaction response:", transactionResponse);

          if (!transactionResponse || !transactionResponse.ok) {
            const errorData = await transactionResponse.json().catch(() => ({}));
            console.error("🚀 Transaction failed:", errorData);
            throw new Error(errorData.detail || "Failed to create transaction");
          }

          console.log("🚀 Transaction created successfully");

        // if (!paymentResponse || !paymentResponse.ok) {
        //   throw new Error("Failed to create payment session");
        // }

        // const paymentData = await paymentResponse.json();

        // For online payments, we don't redirect, just show success
        setPaymentStatus(
          `✅ Online payment completed successfully via ${selectedBank.toUpperCase()}! Transaction has been recorded.`
        );
        
        // Clear payment inputs
        setSelectedBank("");
        setBankAccountNumber("");
        setTransactionNumber("");
        setCardNumber("");
        clearCart();
        fetchProducts(); // Refresh packages
        fetchTransactions();
        setIsCartSummaryModalOpen(false);

        // Show success toast
        toast({
          title: "✅ Transaction Successful!",
          description: `Payment of ₱${total.toFixed(
            2
          )} completed successfully via ${selectedBank.toUpperCase()}. OR: ${orNumber}. Transaction has been recorded.`,
          variant: "default",
        });

        console.log("🚀 Online payment completed successfully");
      }
    }
    } catch (error) {
      console.error("🚀 Payment error:", error);
      setPaymentStatus(`❌ Payment failed: ${error.message}`);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Check for payment result in URL
  useEffect(() => {
    if (!token) return;

    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    const paymentSuccess = urlParams.get("payment_success");
    const paymentCancelled = urlParams.get("payment_cancelled");

    if (sessionId && paymentSuccess) {
      pollPaymentStatus(sessionId);
    } else if (paymentCancelled) {
      setPaymentStatus("Payment was cancelled.");
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [token]);

  // Poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;

    if (attempts >= maxAttempts) {
      setPaymentStatus(
        "Payment status check timed out. Please check your transaction history."
      );
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${BACKEND_URL}/api/payments/status/${sessionId}`
      );
      if (!response || !response.ok) {
        throw new Error("Failed to check payment status");
      }

      const data = await response.json();

      if (data.payment_status === "paid") {
        setPaymentStatus("Payment successful! Thank you for your purchase.");
        clearCart();
        fetchProducts(); // Refresh packages
        fetchTransactions();
        setIsCartSummaryModalOpen(false);
        // Clear URL parameters
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        return;
      } else if (data.status === "expired") {
        setPaymentStatus("Payment session expired. Please try again.");
        // Clear URL parameters
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        return;
      }

      // If payment is still pending, continue polling
      setPaymentStatus("Payment is being processed...");
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setPaymentStatus("Error checking payment status. Please try again.");
    }
  };

  if (!user || !token) {
    return <LoginForm onLogin={handleLogin} />;
  }

  const filteredProducts =
    selectedCategory === "all"
      ? products
      : products.filter((product) => product.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF7D1] to-[#FCE38A]">
      {/* Payment Status Alert */}
      {paymentStatus && (
        <div className="container px-6 pt-4 mx-auto">
          <Alert
            className={
              paymentStatus.includes("successful") ||
              paymentStatus.includes("completed")
                ? "border-green-500 bg-green-50"
                : paymentStatus.includes("failed") ||
                  paymentStatus.includes("cancelled") ||
                  paymentStatus.includes("expired")
                ? "border-red-500 bg-red-50"
                : "border-blue-500 bg-blue-50"
            }
          >
            <AlertDescription className="flex items-center space-x-2">
              {paymentStatus.includes("successful") ||
              paymentStatus.includes("completed") ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : paymentStatus.includes("failed") ||
                paymentStatus.includes("cancelled") ||
                paymentStatus.includes("expired") ? (
                <AlertTriangle className="w-4 h-4 text-red-600" />
              ) : (
                <div className="w-4 h-4 border-b-2 border-blue-600 rounded-full animate-spin"></div>
              )}
              <span>{paymentStatus}</span>
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div className="container px-6 py-6 mx-auto">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <TabsList className="grid w-full max-w-3xl grid-cols-6 bg-white shadow-md">
            <TabsTrigger
              value="pos"
              className="data-[state=active]:bg-cwc-gold data-[state=active]:text-cwc-black hover:bg-cwc-goldLight hover:text-cwc-black data-[state=active]:hover:bg-cwc-gold data-[state=active]:hover:text-cwc-black"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              POS
            </TabsTrigger>
            <TabsTrigger
              value="members"
              className="!text-cwc-black data-[state=active]:bg-cwc-gold data-[state=active]:!text-cwc-black hover:bg-cwc-goldLight hover:!text-cwc-black data-[state=active]:hover:bg-cwc-gold data-[state=active]:hover:!text-cwc-black focus-visible:ring-2 focus-visible:ring-cwc-gold transition-colors"
            >
              <Users className="w-4 h-4 mr-2" />
              Members
            </TabsTrigger>
            {user?.role === "admin" && (
              <TabsTrigger
                value="membership-types"
                className="px-5 data-[state=active]:bg-cwc-gold data-[state=active]:text-cwc-black hover:bg-cwc-goldLight hover:text-cwc-black data-[state=active]:hover:bg-cwc-gold data-[state=active]:hover:text-cwc-black"
              >
                <Crown className="w-4 h-4 mr-2" />
                Types
              </TabsTrigger>
            )}
            {user?.role === "admin" && (
              <TabsTrigger
                value="users"
                className="px-5 data-[state=active]:bg-cwc-gold data-[state=active]:text-cwc-black hover:bg-cwc-goldLight hover:text-cwc-black data-[state=active]:hover:bg-cwc-gold data-[state=active]:hover:text-cwc-black"
              >
                <Shield className="w-4 h-4 mr-2" />
                Users
              </TabsTrigger>
            )}
            <TabsTrigger
              value="packages"
              className="px-5 data-[state=active]:bg-cwc-gold data-[state=active]:text-cwc-black hover:bg-cwc-goldLight hover:text-cwc-black data-[state=active]:hover:bg-cwc-gold data-[state=active]:hover:text-cwc-black"
            >
              <Package className="w-4 h-4 mr-2" />
              Services
            </TabsTrigger>
            <TabsTrigger
              value="reports"
              className="data-[state=active]:bg-cwc-gold data-[state=active]:text-cwc-black hover:bg-cwc-goldLight hover:text-cwc-black data-[state=active]:hover:bg-cwc-gold data-[state=active]:hover:text-cwc-black"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Reports
            </TabsTrigger>
          </TabsList>

          {/* POS Tab */}
          <TabsContent value="pos" className="space-y-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Products Section */}
              <div className="lg:col-span-2">
                <Card className="shadow-xl mb-6">
                  <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-cwc-black to-cwc-gold">
                    <CardTitle className="flex items-center space-x-2">
                      <Package className="w-5 h-5" />
                      <span>Activity Logs</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <table className="w-full table-auto">
                      <thead>
                        <tr>
                          <th className="px-4 py-2 text-left">OR #</th>
                          <th className="px-4 py-2 text-left">Activity</th>
                          <th className="px-4 py-2 text-left">Name</th>
                          <th className="px-4 py-2 text-left">Start Time</th>
                          <th className="px-4 py-2 text-left">End Time</th>
                          <th className="px-4 py-2 text-left">Time Remaining</th>
                          <th className="px-4 py-2 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.slice(0, 5).map((log) => (
                          <tr key={log.id} className="border-t">
                            <td className="px-4 py-2">{log.or_number}</td>
                            <td className="px-4 py-2">
                              {log.productName}
                            </td>
                            
                            <td className="px-4 py-2">{log.member_name}</td>
                            <td className="px-4 py-2">{log.duration} mins</td>
                            <td className="px-4 py-2">
                              {log.time_remaining} mins
                            </td>
                            <td className="px-4 py-2">
                              {log.time_remaining} mins
                            </td>
                            <td className="px-4 py-2">
                              <Button size="sm" variant="outline"
                                onClick={() => {
                                  // Implement extend activity logic here
                                  alert(
                                    `Extend activity for OR #${log.or_number}`
                                  );
                                }}
                              >
                                Extend
                              </Button>
                              <Button size="sm" variant="outline"
                                onClick={() => {
                                  // Implement extend activity logic here
                                  alert(
                                    `Start Time for OR #${log.or_number}`
                                  );
                                }}
                              >
                                Start
                              </Button>
                              <Button size="sm" variant="outline"
                                onClick={() => {
                                  // Implement extend activity logic here
                                  alert(
                                    `End Time for OR #${log.or_number}`
                                  );
                                }}
                              >
                                End
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                <Card className="shadow-xl">
                  <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-cwc-black to-cwc-gold">
                    <CardTitle className="flex items-center space-x-2">
                      <Package className="w-5 h-5" />
                      <span>Products</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Category Filter */}
                    <div className="mb-6">
                      <Select
                        value={selectedCategory}
                        onValueChange={setSelectedCategory}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Categories</SelectItem>
                          {availablePosCategories.map((category) => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Products Grid */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {filteredProducts.map((product) => (
                        <Card
                          key={product.id}
                          className="transition-shadow border-2 cursor-pointer hover:shadow-lg hover:border-cwc-gold"
                          onClick={() => addToCart(product)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <h3 className="font-semibold text-gray-800">
                                {product.name}
                              </h3>
                              <Badge
                                variant="secondary"
                                className="bg-cwc-goldLight text-cwc-black"
                              >
                                ₱{product.price.toFixed(2)}
                              </Badge>
                            </div>
                            <p className="mb-2 text-sm text-gray-600">
                              {product.description}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Cart & Checkout Section */}
              <div className="space-y-6">
                {/* Member Selection */}
                <Card className="border-2 border-cwc-gold shadow-xl">
                  <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-cwc-black to-cwc-gold">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Users className="w-5 h-5" />
                        <span>Select Client</span>
                      </div>
                      <Badge
                        variant="secondary"
                        className="text-cwc-black bg-cwc-gold"
                        style={{ pointerEvents: "none" }}
                      >
                        Required
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <Tabs
                      value={selectedMember?.type || "member"}
                      onValueChange={(tab) => {
                        if (tab === "walkin") {
                          setSelectedMember({
                            type: "walkin",
                            name: "",
                            discount_percentage: 0,
                            membership_type: "Walk-in",
                          });
                          setMemberSearch("");
                          setMemberSearchResults([]);
                        } else {
                          setSelectedMember(null);
                        }
                      }}
                    >
                      <TabsList className="mb-4 grid grid-cols-2 w-full">
                        <TabsTrigger
                          value="member"
                          className="data-[state=active]:bg-cwc-gold data-[state=active]:text-cwc-black"
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Member
                        </TabsTrigger>
                        <TabsTrigger
                          value="walkin"
                          className="data-[state=active]:bg-cwc-goldDark data-[state=active]:text-cwc-black"
                        >
                          <User className="w-4 h-4 mr-2" />
                          Walk-in
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="member">
                        <div className="space-y-3">
                          <div className="relative">
                            <Search className="absolute w-4 h-4 text-gray-400 left-3 top-3" />
                            <Input
                              ref={memberSearchInputRef}
                              placeholder="Search members by name, email, or phone..."
                              value={memberSearch}
                              onChange={(e) => setMemberSearch(e.target.value)}
                              className="pl-8"
                            />
                          </div>
                          {memberSearchResults.length > 0 && (
                            <div className="relative z-50 overflow-y-auto bg-white border rounded-lg shadow-sm max-h-40">
                              {memberSearchResults.map((member) => (
                                <div
                                  key={member.id}
                                  className="p-3 transition-colors border-b cursor-pointer hover:bg-cwc-gold/20 last:border-b-0"
                                  onClick={() => {
                                    setSelectedMember(member);
                                    setMemberSearch("");
                                    setMemberSearchResults([]);
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="font-medium text-gray-800">
                                        {member.name}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        {member.email}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        {member.phone}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <Badge
                                        variant="outline"
                                        className="text-cwc-goldDark border-cwc-gold"
                                      >
                                        {member.membership_type}
                                      </Badge>
                                      <div className="text-sm font-medium text-cwc-goldDark">
                                        {member.discount_percentage}% discount
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {memberSearch.length > 0 && memberSearchResults.length === 0 && (
                            <div className="space-y-3">
                              <Alert className="border-cwc-gold bg-cwc-gold/10">
                                <AlertTriangle className="w-4 h-4 text-cwc-goldDark" />
                                <AlertDescription className="text-cwc-goldDark">
                                  No members found for "{memberSearch}".
                                </AlertDescription>
                              </Alert>
                              <div className="text-center">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setActiveTab("members");
                                  }}
                                  className="text-cwc-goldDark border-cwc-gold hover:bg-cwc-gold/20"
                                >
                                  <UserPlus className="w-4 h-4 mr-2" />
                                  Register New Member
                                </Button>
                              </div>
                            </div>
                          )}
                          {selectedMember && (
                            <div className="p-4 border rounded-lg bg-cwc-goldLight border-cwc-gold">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <Check className="w-5 h-5 text-cwc-goldDark" />
                                  <span className="font-semibold text-cwc-black">
                                    Selected Member
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedMember(null);
                                    setMemberSearch("");
                                  }}
                                  className="text-red-500 hover:bg-red-50"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                              <div className="space-y-1">
                                <div className="font-medium text-cwc-black">
                                  {selectedMember.name}
                                </div>
                                <div className="text-sm text-gray-700">
                                  {selectedMember.email}
                                </div>
                                <div className="text-sm text-gray-700">
                                  {selectedMember.phone}
                                </div>
                                <div className="flex items-center mt-2 space-x-2">
                                  <Badge
                                    variant="outline"
                                    className="text-cwc-black border-cwc-gold"
                                  >
                                    {selectedMember.membership_type}
                                  </Badge>
                                  <Badge
                                    variant="outline"
                                    className="text-cwc-goldDark border-cwc-gold"
                                  >
                                    {selectedMember.discount_percentage}% Discount
                                    Applied
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </TabsContent>
                      <TabsContent value="walkin">
                        <div className="space-y-3">
                          <Label htmlFor="walkinName">Walk-in Client Name</Label>
                          <Input
                            ref={walkinInputRef}
                            id="walkinName"
                            placeholder="Enter walk-in client name"
                            value={selectedMember?.type === "walkin" ? selectedMember.name : ""}
                            onChange={(e) =>
                              setSelectedMember({
                                type: "walkin",
                                name: e.target.value,
                                discount_percentage: 0,
                                membership_type: "Walk-in",
                              })
                            }
                            required
                          />
                          {selectedMember?.type === "walkin" && selectedMember.name && (
                            <div className="p-4 border border-cwc-goldDark rounded-lg bg-cwc-gold/10">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <Check className="w-5 h-5 text-cwc-goldDark" />
                                  <span className="font-semibold text-cwc-goldDark">
                                    Walk-in Client Selected
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setSelectedMember(null)}
                                  className="text-red-500 hover:bg-red-50"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                              <div className="space-y-1">
                                <div className="font-medium text-cwc-goldDark">
                                  {selectedMember.name}
                                </div>
                                <Badge
                                  style={{ pointerEvents: "none" }}
                                  variant="secondary"
                                  className="text-cwc-goldDark bg-cwc-gold/20"
                                >
                                  Walk-in
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className="mx-2 text-gray-600 border-gray-300"
                                >
                                  No Discount
                                </Badge>
                              </div>
                            </div>
                          )}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
                <Card className="shadow-xl">
                  <CardHeader className="text-white rounded-t-lg bg-gradient-to-r from-cwc-black to-cwc-gold">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <ShoppingCart className="w-5 h-5" />
                        <span>Cart ({cart.length})</span>
                      </div>
                      {cart.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearCart}
                          className="text-white hover:bg-white/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    {cart.length === 0 ? (
                      <div className="py-8 text-center">
                        <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="mb-2 text-gray-500">No items in cart</p>
                        <div className="space-y-1 text-sm text-gray-400">
                          <p>1. Add products to cart</p>
                          <p>2. Select a member (required)</p>
                          <p>3. Choose payment method</p>
                          <p>4. Complete purchase</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {cart.map((item) => {
                          const product = products.find((p) => p.id === item.product_id);
                          const stock = product?.stock_quantity ?? Infinity;
                          const atMax = item.quantity >= stock;
                          return (
                            <div
                              key={item.product_id}
                              className="flex items-center justify-between p-3 rounded-lg bg-gray-50"
                            >
                              <div className="flex-1">
                                <div className="text-sm font-medium">
                                  {item.product_name}
                                </div>
                                <div className="text-sm text-gray-600">
                                  ₱{item.price.toFixed(2)} each
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    updateCartItemQuantity(
                                      item.product_id,
                                      item.quantity - 1
                                    )
                                  }
                                  className="w-8 h-8 p-0"
                                >
                                  <Minus className="w-3 h-3" />
                                </Button>
                                <span className="w-8 font-medium text-center">
                                  {item.quantity}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    updateCartItemQuantity(
                                      item.product_id,
                                      item.quantity + 1
                                    )
                                  }
                                  disabled={atMax}
                                  title={atMax && isFinite(stock) ? `Max ${stock} in stock` : undefined}
                                  className={`w-8 h-8 p-0 ${atMax ? "opacity-50 cursor-not-allowed" : ""}`}
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeFromCart(item.product_id)}
                                  className="w-8 h-8 p-0 text-red-500 hover:bg-red-50"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}

                        <Separator className="my-4" />

                        {/* Totals */}
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span>Subtotal:</span>
                            <span>₱{subtotal.toFixed(2)}</span>
                          </div>
                          {discountAmount > 0 && (
                            <div className="flex justify-between text-cwc-goldDark">
                              <span>
                                Discount ({selectedMember.discount_percentage}
                                %):
                              </span>
                              <span>-₱{discountAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <Separator />
                          <div className="flex justify-between text-lg font-bold">
                            <span>Total:</span>
                            <span>₱{total.toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Payment Method Selection */}
                        <div className="space-y-3">
                          <label className="text-sm font-medium">
                            Payment Method:
                          </label>
                          <Select
                            value={paymentMethod}
                            onValueChange={setPaymentMethod}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cash">💵 Cash</SelectItem>
                              <SelectItem value="card">
                                💳 Credit/Debit Card
                              </SelectItem>
                              <SelectItem value="online">
                                🌐 Online Payment
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Checkout Button */}
                        <Button
                          className={`w-full font-semibold py-3 ${
                            !selectedMember || cart.length === 0
                              ? "bg-gray-400 hover:bg-gray-400 cursor-not-allowed text-white"
                              : "bg-cwc-gold hover:bg-cwc-goldDark text-black"
                          }`}
                          onClick={() => {
                            console.log("🔍 Review Purchase button clicked");
                            console.log("🔍 selectedMember:", selectedMember);
                            console.log("🔍 cart.length:", cart.length);
                            if (selectedMember && cart.length > 0) {
                              console.log("🔍 Opening modal...");
                              setIsCartSummaryModalOpen(true);
                            }
                          }}
                          disabled={
                            isProcessingPayment ||
                            !selectedMember ||
                            cart.length === 0
                          }
                        >
                          {isProcessingPayment ? (
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 border-b-2 border-white rounded-full animate-spin"></div>
                              <span>Processing...</span>
                            </div>
                          ) : !selectedMember ? (
                            <div className="flex items-center space-x-2">
                              <AlertTriangle className="w-4 h-4" />
                              <span>Select Member to Continue</span>
                            </div>
                          ) : cart.length === 0 ? (
                            <div className="flex items-center space-x-2">
                              <ShoppingCart className="w-4 h-4" />
                              <span>Add Items to Cart</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <CreditCard className="w-4 h-4" />
                              <span>Proceed to Payment</span>
                            </div>
                          )}
                        </Button>

                        {!selectedMember && cart.length > 0 && (
                          <Alert className="mt-3 border-red-500 bg-red-50">
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                            <AlertDescription className="text-red-700">
                              <strong>Member Required:</strong> Please search
                              and select a member from the list above to
                              complete this purchase.
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members">
            <MemberManagement token={token} user={user} />
          </TabsContent>

          {/* Membership Types Tab - Admin Only */}
          {user?.role === "admin" && (
            <TabsContent value="membership-types">
              <MembershipTypeManagement token={token} user={user} />
            </TabsContent>
          )}

          {/* Users Tab - Admin Only */}
          {user?.role === "admin" && (
            <TabsContent value="users">
              <UserManagement token={token} user={user} />
            </TabsContent>
          )}

          {/* Inventory Tab */}
          <TabsContent value="packages">
            <ProductManagement
              token={token}
              user={user}
              onProductsChanged={() => {
                fetchProducts();
                // fetchLowStockItems();
              }}
            />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <ReportManagement token={token} user={user} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Cart Summary Modal */}
      <Dialog
        open={isCartSummaryModalOpen}
        onOpenChange={setIsCartSummaryModalOpen}
      >
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader className="p-4 mb-4 -m-6 text-white rounded-t-lg bg-gradient-to-r from-cwc-black to-cwc-gold">
            <DialogTitle className="flex items-center space-x-2 text-lg">
              <ShoppingCart className="w-5 h-5" />
              <span>Transaction Summary</span>
            </DialogTitle>
            <DialogDescription className="text-sm text-yellow-100">
              Review and confirm your transaction
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Member Information - Compact */}
            <div className="p-3 border rounded-lg bg-cwc-goldLight border-cwc-gold">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-cwc-goldDark" />
                  <span className="font-medium text-cwc-black">Member</span>
                </div>
                <Badge
                  variant="outline"
                  className="text-cwc-goldDark border-cwc-gold"
                >
                  {selectedMember?.discount_percentage}% off
                </Badge>
              </div>
              <div className="mt-2">
                <div className="text-sm font-semibold text-cwc-black">
                  {selectedMember?.name}
                </div>
                <div className="text-xs text-gray-700">
                  {selectedMember?.membership_type}
                </div>
              </div>
            </div>

            {/* Cart Items - Compact */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Package className="w-4 h-4 text-gray-600" />
                  <span className="font-medium text-gray-800">
                    Items ({cart.length})
                  </span>
                </div>
              </div>
              <div className="space-y-2 overflow-y-auto max-h-32">
                {cart.map((item) => (
                  <div
                    key={item.product_id}
                    className="flex items-center justify-between p-2 rounded-md bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {item.product_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        ₱{item.price.toFixed(2)} × {item.quantity}
                      </div>
                    </div>
                    <span className="ml-2 text-sm font-semibold text-cwc-goldDark">
                      ₱{(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Method - Compact */}
            <div className="p-3 border rounded-lg bg-cwc-goldLight border-cwc-gold">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <CreditCard className="w-4 h-4 text-cwc-goldDark" />
                  <span className="font-medium text-cwc-black">Payment</span>
                </div>
                <Badge
                  variant="outline"
                  className="text-cwc-goldDark border-cwc-gold"
                >
                  {paymentMethod === "cash"
                    ? "💵 Cash"
                    : paymentMethod === "card"
                    ? "💳 Card"
                    : paymentMethod === "online"
                    ? "🌐 Online"
                    : paymentMethod}
                </Badge>
              </div>

              {/* OR Number - Required for all payments */}
              <div className="mt-3">
                <Label htmlFor="orNumber" className="text-sm font-medium">
                  OR Number
                </Label>
                <Input
                  id="orNumber"
                  value={orNumber}
                  onChange={(e) => setOrNumber(e.target.value)}
                  placeholder="Enter OR number from booklet"
                  className="mt-1"
                />
              </div>

              {/* Card payment fields - only shown when card payment is selected */}
              {paymentMethod === "card" && (
                <div className="mt-3 space-y-3">
                  <div>
                    <Label htmlFor="cardNumber" className="text-sm font-medium">
                      Card Number
                    </Label>
                    <Input
                      id="cardNumber"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                      placeholder="Enter card number"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="transactionNumber"
                      className="text-sm font-medium"
                    >
                      Transaction Number
                    </Label>
                    <Input
                      id="transactionNumber"
                      value={transactionNumber}
                      onChange={(e) => setTransactionNumber(e.target.value)}
                      placeholder="Enter transaction number"
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              {/* Online payment fields - only shown when online payment is selected */}
              {paymentMethod === "online" && (
                <div className="mt-3 space-y-3">
                  <div>
                    <Label htmlFor="bankSelect" className="text-sm font-medium">
                      Select Bank/Payment Provider
                    </Label>
                    <Select value={selectedBank} onValueChange={setSelectedBank}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Choose payment provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {onlinePaymentBanks.map((bank) => (
                          <SelectItem key={bank.value} value={bank.value}>
                            <div className="flex items-center space-x-2">
                              <img
                                src={bank.logo}
                                alt={bank.label}
                                className="w-6 h-6 object-contain"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                              <span>{bank.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="bankAccountNumber" className="text-sm font-medium">
                      Account Number
                    </Label>
                    <Input
                      id="bankAccountNumber"
                      value={bankAccountNumber}
                      onChange={(e) => setBankAccountNumber(e.target.value)}
                      placeholder="Enter account number"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="onlineTransactionNumber"
                      className="text-sm font-medium"
                    >
                      Transaction Reference Number
                    </Label>
                    <Input
                      id="onlineTransactionNumber"
                      value={transactionNumber}
                      onChange={(e) => setTransactionNumber(e.target.value)}
                      placeholder="Enter transaction reference number"
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Totals - Compact */}
            <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">₱{subtotal.toFixed(2)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-cwc-goldDark">
                    <span>Discount:</span>
                    <span className="font-medium">
                      -₱{discountAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                <Separator className="my-2" />
                <div className="flex justify-between font-bold text-cwc-goldDark">
                  <span>Total:</span>
                  <span>₱{total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons - Compact */}
            <div className="flex pt-2 space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsCartSummaryModalOpen(false)}
                className="flex-1 text-sm text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button
                onClick={processPayment}
                disabled={isProcessingPayment}
                className="flex-1 text-sm text-black bg-cwc-gold hover:bg-cwc-goldDark"
              >
                {isProcessingPayment ? (
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 border-b-2 border-black rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1">
                    <Check className="w-4 h-4" />
                    <span>Confirm Payment</span>
                  </div>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Toaster />
    </div>
  );
}
